import { Alert, Button, List, Space, Tag, Typography } from 'antd';
import { AimOutlined } from '@ant-design/icons';
import type { ScreenDiagnostic } from '@/api/screens';

const { Text } = Typography;

/**
 * The compile gate's verdict, rendered whole: EVERY diagnostic at once (the validator enumerates,
 * never fails-fast), each with its JSON-pointer path and — when the buffer parses — a locate
 * button that selects the offending node in the editor. WARNINGs never block; ERRORs mean the
 * gate refuses to store.
 */
export function DiagnosticsPanel({
  diagnostics,
  onLocate,
  located,
}: {
  diagnostics: ScreenDiagnostic[];
  /** Focus the editor on this pointer; absent = the buffer doesn't parse, locate is off. */
  onLocate?: (path: string) => void;
  /** Extra line/col info per pointer, computed by the page against the live buffer. */
  located?: (path: string) => string | null;
}) {
  if (diagnostics.length === 0) {
    return (
      <Alert
        type="success"
        showIcon
        message="The gate passes this definition"
        description="No errors, no warnings — it can be drafted and published as-is."
      />
    );
  }
  const errors = diagnostics.filter((d) => d.severity === 'ERROR').length;
  return (
    <div>
      <Alert
        type={errors > 0 ? 'error' : 'warning'}
        showIcon
        message={
          errors > 0
            ? `${errors} error${errors === 1 ? '' : 's'} — the gate refuses to store this definition`
            : 'Warnings only — publishable, but read them'
        }
        style={{ marginBottom: 10 }}
      />
      <List
        size="small"
        dataSource={diagnostics}
        renderItem={(d) => {
          const where = located?.(d.path) ?? null;
          return (
            <List.Item
              style={{ paddingInline: 0, alignItems: 'flex-start' }}
              actions={
                onLocate && d.path
                  ? [
                      <Button
                        key="locate"
                        size="small"
                        type="text"
                        icon={<AimOutlined />}
                        onClick={() => onLocate(d.path)}
                      >
                        Locate
                      </Button>,
                    ]
                  : undefined
              }
            >
              <Space direction="vertical" size={2} style={{ width: '100%' }}>
                <Space size={6} wrap>
                  <Tag color={d.severity === 'ERROR' ? 'error' : 'warning'} style={{ fontSize: 11 }}>
                    {d.severity}
                  </Tag>
                  <Text code style={{ fontSize: 11.5 }}>
                    {d.code}
                  </Text>
                  {d.path && (
                    <Text type="secondary" className="ol-mono" style={{ fontSize: 11.5 }}>
                      {d.path}
                      {where ? ` (${where})` : ''}
                    </Text>
                  )}
                </Space>
                <Text style={{ fontSize: 12.5 }}>{d.message}</Text>
              </Space>
            </List.Item>
          );
        }}
      />
    </div>
  );
}
